import { getLovLabel } from '@zoryn/hooks'
import { isActiveRule, isValuelessOperator } from './rule-utils'
import {
  RULE_ERROR_MESSAGE_KEY,
  RULE_RESULT_VALUE_KEY,
  type RuleCondition,
  type RuleContentType,
  type RuleGroupState,
  type RuleResultSchema,
  type RuleResultValue,
  type SystemParamField,
} from './rules'

export interface RuleDisplayCondition {
  id: string
  field: string
  columnTitle: string
  operator: string
  operatorLabel: string
  valueLabel: string
  logic: string
}

export interface RuleDisplayGroup {
  id: string
  logic: 'and' | 'or'
  groupLogic: 'and' | 'or'
  conditions: RuleDisplayCondition[]
}

function resolveFieldTitle(fields: SystemParamField[], field: string) {
  return fields.find((item) => item.field === field)?.name || field
}

function resolveFieldMeta(fields: SystemParamField[], field: string) {
  return fields.find((item) => item.field === field)
}

function formatOperatorLabel(operator: string, t: (key: string) => string) {
  const key = `z.table.operator.${operator}`
  const label = t(key)
  return label === key ? operator : label
}

function formatValueLabel(cond: RuleCondition, field?: SystemParamField) {
  if (isValuelessOperator(cond.operator)) {
    return ''
  }
  const value = cond.value
  if (value === null || value === undefined || value === '') {
    return ''
  }
  if (cond.type === 'switch') {
    return value === true || value === 'true' ? '开' : '关'
  }
  if (cond.type === 'lov' && cond.dataSource) {
    return getLovLabel(cond.dataSource, value) || String(value)
  }
  if (cond.type === 'table') {
    return cond.valueLabel || String(value)
  }
  return String(value)
}

function formatConditionText(cond: RuleDisplayCondition) {
  if (cond.valueLabel) {
    return `${cond.columnTitle} ${cond.operatorLabel} ${cond.valueLabel}`
  }
  return `${cond.columnTitle} ${cond.operatorLabel}`
}

export function buildRuleDisplayGroups(
  groups: RuleGroupState[],
  fields: SystemParamField[],
  t: (key: string) => string
): RuleDisplayGroup[] {
  return groups
    .map((group) => ({
      id: group.id,
      logic: (group.logic || 'and') as 'and' | 'or',
      groupLogic: (group.groupLogic || 'or') as 'and' | 'or',
      conditions: group.conditions
        .filter(isActiveRule)
        .map((cond) => {
          const field = resolveFieldMeta(fields, cond.field)
          return {
            id: cond.id,
            field: cond.field,
            columnTitle: resolveFieldTitle(fields, cond.field),
            operator: cond.operator,
            operatorLabel: formatOperatorLabel(cond.operator, t),
            valueLabel: formatValueLabel(cond, field),
            logic: cond.logic || 'and',
          }
        }),
    }))
    .filter((group) => group.conditions.length > 0)
}

export function formatRuleDisplayText(
  groups: RuleDisplayGroup[],
  t: (key: string) => string
) {
  return groups
    .map((group, groupIndex) => {
      const inner = group.conditions
        .map((cond, condIndex) => {
          const prefix =
            condIndex > 0 ? ` ${t(`z.table.logic.${cond.logic}`)} ` : ''
          return `${prefix}${formatConditionText(cond)}`
        })
        .join('')
      const wrapped = group.conditions.length > 1 ? `(${inner})` : inner
      if (groupIndex === 0) {
        return wrapped
      }
      return ` ${t(`z.table.logic.${group.groupLogic}`)} ${wrapped}`
    })
    .join('')
}

export function formatRuleResultSuffix(
  content: RuleContentType,
  resultJson: RuleResultValue,
  ruleResult?: RuleResultSchema
): string {
  if (content === 'boolean') {
    return ''
  }
  if (content === 'error') {
    const message = resultJson[RULE_ERROR_MESSAGE_KEY]
    if (message == null || message === '') {
      return ''
    }
    return ` error: ${message}`
  }
  const value = resultJson[RULE_RESULT_VALUE_KEY]
  if (value == null || value === '') {
    return ''
  }
  if (ruleResult?.type === 'number') {
    return ` = ${value}元`
  }
  if (ruleResult?.type === 'switch') {
    const text = value === true || value === 'true' ? '开' : '关'
    return ` = ${text}`
  }
  return ` = ${value}`
}

/** 条件可读描述 + 结果后缀，如 (PCB层数 包含 2 并且 部件 包含 22) = 8元 */
export function formatRuleDescription(
  conditionText: string,
  content: RuleContentType,
  resultJson: RuleResultValue,
  ruleResult?: RuleResultSchema
): string {
  if (!conditionText) {
    return ''
  }
  const suffix = formatRuleResultSuffix(content, resultJson, ruleResult)
  if (content === 'boolean' && !suffix) {
    return `${conditionText} => 通过`
  }
  return `${conditionText}${suffix}`
}
